<template>
  <div class="flex flex-col md:flex-row items-start w-full md:max-w-lg gap-4">
    <form @submit="submit" class="w-full">
      <div v-if="formError" class="text-sm font-medium text-error-600 text-center mb-4">{{ formError }}</div>
      <div v-if="formSuccess" class="text-sm font-medium text-success-600 text-center mb-4">{{ formSuccess }}</div>
      <div class="w-full flex flex-col md:flex-row items-start gap-2">
        <FormField v-slot="{ componentField }" name="email">
          <FormItem class="flex-1 w-full">
            <FormControl>
              <Input
                type="email"
                placeholder="Enter your email"
                class="h-10 sm:h-11 text-sm sm:text-base w-full"
                v-bind="componentField"
                aria-label="Email address"
              />
            </FormControl>
          </FormItem>
        </FormField>
        <Button
          type="submit"
          :disabled="isLoading"
          :variant="props.variant == 'dark' ? 'secondary' : 'default'"
          class="h-10 sm:h-11 inline-flex w-full md:w-fit cursor-pointer"
          ><span v-if="isLoading">Subscribing...</span> <span v-else>Subscribe</span></Button
        >
      </div>
      <p class="text-xs text-gray-500 mt-2">
        We care about your data in our
        <a
          href="/privacy-policy"
          :class="props.variant == 'dark' ? 'text-secondary underline' : 'text-primary underline'"
          >privacy policy</a
        >
      </p>
    </form>
  </div>
</template>

<script setup lang="ts">
import { trackMetaEvent } from '@/analytics/metaPixel';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { subscribe } from '@/services/campaignMonitor';
import { useThemeStore } from '@/stores/theme';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { ref } from 'vue';
import * as z from 'zod';

const themeStore = useThemeStore();

interface Props {
  variant?: 'light' | 'dark';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'light',
});

const validationSchema = toTypedSchema(
  z.object({
    email: z.string().email('Please enter a valid email address'),
  }),
);

interface FormValues {
  email: string;
}

const form = useForm({ validationSchema });
const isLoading = ref(false);
const formError = ref('');
const formSuccess = ref('');

const submit = form.handleSubmit(async (values: FormValues) => {
  try {
    isLoading.value = true;
    await subscribe(values.email, themeStore.mode);
    formSuccess.value = 'Thank you for subscribing!';
    formError.value = '';
    form.resetForm();
    addMetric('subscribe_form', { status: 'success', channel: 'website' });
    trackMetaEvent('CompleteRegistration', 'Consumer Signup');
  } catch (error) {
    createException(error);
    addMetric('subscribe_form', { status: 'error', channel: 'website' });
    formError.value = 'Failed to subscribe. Please try again.';
  } finally {
    isLoading.value = false;
  }
});
</script>
